import React, { useEffect, useState } from "react";
import { PaperClipIcon, XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import PDFViewer from "@/components/PDFViewer";
import { useTranslation } from "react-i18next";

const FileModal = ({ isOpen, fileUrl, type, onClose }) => {
    const { t } = useTranslation('chat');
    const [isLoading, setIsLoading] = useState(true);

    // Close on Escape key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    if (!isOpen) return null;

    const isImage = fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || type === "image";
    const isPDF = fileUrl?.toLowerCase().endsWith(".pdf") || type === "pdf";

const getFileName = (fileUrl) => {
  if (!fileUrl) return "file";

  try {
    const url = new URL(fileUrl);
    const pathname = url.pathname; 
    const name = pathname.split("/").pop(); 
    return name || "file";
  } catch (e) {
    console.warn("Invalid URL:", e);
    return "file";
  }
};




  const handleDownload = async (e) => {
        e.preventDefault();
        const downloadName =getFileName(fileUrl);

        try {
            const response = await fetch(fileUrl, {
                method: 'GET',
                headers: {}
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            
            link.href = url;
            link.download = downloadName; // Use the safe variable here
            
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Download failed:', error);
            // Fallback: Open in new tab if the fetch fails
            window.open(fileUrl, '_blank');
        }
    }
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 md:p-10 transition-opacity"
            onClick={onClose}
        >
            <div
                className="relative bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden max-w-5xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Fixed Header Section */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 z-10">
                    <div className="flex flex-col min-w-0">
                        {/* <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {isPDF ? t('pdfDocument') : t('filePreview')}
                        </h3> */}
                        {/* <p className="text-[10px] text-gray-500 truncate max-w-[200px] md:max-w-md italic">
                            {fileUrl}
                        </p> */}
                    </div>

                    <div className="flex items-center gap-2">
                        <a
                            href="#"
                            onClick={handleDownload}
                            download                           
                            rel="noreferrer"
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-zinc-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 text-gray-700 dark:text-gray-300 hover:text-emerald-700 dark:hover:text-emerald-400 rounded-lg transition-colors text-xs font-medium"
                            title={t('download')}
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">{t('download')}</span>
                        </a>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-500 hover:text-red-600 rounded-full transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Content Area - Using flex-col and overflow-hidden to manage internal scrolling */}
                <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-zinc-950">
                    {isImage ? (
                        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
                            <div className="relative group">
                                {isLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-zinc-800 animate-pulse rounded-lg" />
                                )}
                                <img
                                    src={fileUrl}
                                    alt="Preview"
                                    onLoad={() => setIsLoading(false)}
                                    className={`max-h-[70vh] w-auto rounded-lg shadow-lg object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                                />
                            </div>
                        </div>
                    ) : isPDF ? (
                        /* PDF WRAPPER: This creates the scrollable zone after the header */
                        <div className="flex-1 overflow-y-auto w-full pt-2">
                            <div className="min-h-full flex justify-center p-4">
                                <PDFViewer url={fileUrl} />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex items-center justify-center p-12">
                            <div className="text-center">
                                <div className="bg-gray-100 dark:bg-zinc-800 p-6 rounded-full inline-block mb-4 border border-gray-200 dark:border-zinc-700">
                                    <PaperClipIcon className="h-12 w-12 text-gray-400" />
                                </div>
                                <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{t('previewUnavailable')}</h4>
                                <p className="text-gray-500 mt-2 mb-8 max-w-xs mx-auto">{t('previewUnavailableDetail')}</p>
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center bg-[#065C36] text-white px-8 py-3 rounded-xl font-medium hover:bg-[#054d2d] transition-transform active:scale-95 shadow-lg shadow-emerald-900/20"
                                >
                                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                                    {t('downloadToView')}
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FileModal;